<template>
  <div class="row">
    <div class="col-md-12 col-xxl-12 col-sm-12 mb-10">
      <div class="card mb-25 border-0 rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
          <!-- En-tête avec bouton retour -->
          <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 mb-4">
            <router-link to="/liste-emplois" 
              class="btn btn-primary transition border-0 lh-1 fw-medium">
              <i class="flaticon-left-arrow lh-1 me-1 position-relative top-2"></i>
              <span class="position-relative">Retour</span>
            </router-link>
            <h4 class="mb-0">Détails de l'emploi du temps</h4>
          </div>

          <!-- Informations générales sur 2 colonnes -->
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="info-card p-3 border rounded bg-light">
                <h6 class="text-primary mb-3">
                  <i class="flaticon-calendar me-2"></i>Informations générales
                </h6>
                <div class="row mb-2">
                  <div class="col-5"><strong>Type d'emploi:</strong></div>
                  <div class="col-7">{{ emploiDuTemps?.typeEmploi?.libelle || 'N/A' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>Date de début:</strong></div>
                  <div class="col-7">{{ emploiDuTemps?.dateDebut ? format_date(emploiDuTemps.dateDebut) : 'N/A' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>Date de fin:</strong></div>
                  <div class="col-7">{{ emploiDuTemps?.dateFin ? format_date(emploiDuTemps.dateFin) : 'N/A' }}</div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="info-card p-3 border rounded bg-light">
                <h6 class="text-primary mb-3">
                  <i class="flaticon-clock me-2"></i>Informations sur cours
                </h6>
                <div class="row mb-2">
                  <div class="col-5"><strong>Nombre de cours:</strong></div>
                  <div class="col-7">
                    <span class="badge bg-success">{{ coursGroupes?.length || 0 }}</span>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-5"><strong>Date de création:</strong></div>
                  <div class="col-7">{{ emploiDuTemps?.createdAt ? format_date(emploiDuTemps.createdAt) : 'N/A' }}</div>
                </div>
                <div class="row mb-2" v-if="emploiDuTemps?.updatedAt && emploiDuTemps?.updatedAt !== emploiDuTemps?.createdAt">
                  <div class="col-5"><strong>Dernière modification:</strong></div>
                  <div class="col-7">{{ format_date(emploiDuTemps.updatedAt) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section des cours -->
          <div class="row">
            <div class="col-md-12">
              <h5 class="text-primary mb-3 border-bottom pb-2">
                <i class="flaticon-book me-2"></i>Liste des cours ({{ coursGroupes?.length || 0 }})
              </h5>
              
              <div v-if="coursGroupes && coursGroupes.length > 0" class="cours-container">
                <div v-for="(coursGroupe, index) in coursGroupes" :key="index" 
                     class="cours-card border rounded p-3 mb-3 shadow-sm bg-white">
                  <!-- En-tête du cours -->
                  <div class="cours-header bg-primary text-white p-2 rounded mb-3">
                    <h6 class="mb-0">
                      <i class="flaticon-book me-2"></i>Cours {{ index + 1 }} - {{ coursGroupe.matiere }}
                    </h6>
                  </div>

                  <!-- Informations du cours sur 3 colonnes -->
                  <div class="row mb-3">
                    <div class="col-md-4">
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Matière</small>
                        <strong>{{ coursGroupe.matiere }}</strong>
                      </div>
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Filière</small>
                        <strong>{{ coursGroupe.filiere }}</strong>
                      </div>
                    </div>
                    
                    <div class="col-md-4">
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Niveau</small>
                        <strong>{{ coursGroupe.niveau }}</strong>
                      </div>
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Professeur</small>
                        <strong>{{ coursGroupe.professeur }}</strong>
                      </div>
                    </div>
                    
                    <div class="col-md-4">
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Classe</small>
                        <strong>{{ coursGroupe.classe }}</strong>
                      </div>
                      <div class="info-item mb-2">
                        <small class="text-muted d-block">Mode</small>
                        <span :class="coursGroupe.estEnLigne ? 'badge bg-info' : 'badge bg-secondary'">
                          {{ coursGroupe.estEnLigne ? 'En ligne' : 'Présentiel' }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Créneaux horaires -->
                  <div class="creneaux-section border-top pt-3">
                    <h6 class="text-secondary mb-3">
                      <i class="flaticon-clock me-2"></i>
                      Créneaux horaires ({{ coursGroupe.creneaux.length }})
                    </h6>
                    
                    <div class="row">
                      <div v-for="(creneau, creneauIndex) in coursGroupe.creneaux" 
                           :key="creneauIndex" 
                           class="col-md-6 col-lg-4 mb-2">
                        <div class="creneau-card border rounded p-2 bg-light">
                          <div class="d-flex align-items-center">
                            <span class="badge bg-primary me-2">{{ creneauIndex + 1 }}</span>
                            <div class="creneau-details flex-grow-1">
                              <div class="fw-bold text-dark">{{ creneau.jour }}</div>
                              <div class="text-muted small">
                                {{ creneau.heureDebut }} - {{ creneau.heureFin }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="alert alert-info">
                <i class="flaticon-info me-2"></i>
                Aucun cours trouvé pour cet emploi du temps.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { error, format_date } from "@/utils/utils";
import { useRoute } from "vue-router";
import ApiService from "@/services/ApiService";

export default defineComponent({
  name: "ViewEmploiDuTemps",
  setup() {
    const route = useRoute();
    const emploiDuTemps = ref<any>(null);

    // Computed property pour regrouper les cours
    const coursGroupes = computed(() => {
      if (!emploiDuTemps.value?.cours || emploiDuTemps.value.cours.length === 0) {
        return [];
      }

      const groupes = new Map();

      emploiDuTemps.value.cours.forEach((cours: any) => {
        // Clé de regroupement basée sur les caractéristiques communes
        const cleGroupe = `${cours.filiereNiveauMatiere?.matiere?.libelle || ''}_${cours.filiereNiveauMatiere?.filiere?.libelle || ''}_${cours.filiereNiveauMatiere?.niveau?.libelle || ''}_${cours.professeur?.nom || ''}_${cours.professeur?.prenom || ''}_${cours.classe?.libelle || ''}_${cours.estEnLigne}`;

        if (!groupes.has(cleGroupe)) {
          // Créer un nouveau groupe
          groupes.set(cleGroupe, {
            matiere: cours.filiereNiveauMatiere?.matiere?.libelle || 'N/A',
            filiere: cours.filiereNiveauMatiere?.filiere?.libelle || 'N/A',
            niveau: cours.filiereNiveauMatiere?.niveau?.libelle || 'N/A',
            professeur: `${cours.professeur?.nom || ''} ${cours.professeur?.prenom || ''}`.trim() || 'N/A',
            classe: cours.classe?.libelle || 'N/A',
            estEnLigne: cours.estEnLigne || false,
            creneaux: []
          });
        }

        // Ajouter le créneau horaire au groupe
        groupes.get(cleGroupe).creneaux.push({
          jour: cours.jour,
          heureDebut: cours.heureDebut,
          heureFin: cours.heureFin
        });
      });

      // Trier les créneaux de chaque groupe par jour et heure
      const joursOrdre = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
      
      groupes.forEach((groupe) => {
        groupe.creneaux.sort((a: any, b: any) => {
          const jourA = joursOrdre.indexOf(a.jour);
          const jourB = joursOrdre.indexOf(b.jour);
          
          if (jourA !== jourB) {
            return jourA - jourB;
          }
          
          // Si même jour, trier par heure de début
          return a.heureDebut.localeCompare(b.heureDebut);
        });
      });

      return Array.from(groupes.values());
    });

    function getEmploiDuTemps(id: string) {
      return ApiService.get(`/emplois-du-temps/${id}`)
        .then(({ data }) => {
          emploiDuTemps.value = data.data;
          console.log('Emploi du temps complet:', data.data);
          console.log('Cours groupés:', coursGroupes.value);
        })
        .catch(({ response }) => {
          error(response.data.message);
        });
    }

    onMounted(() => {
      const id = route.params.id as string;
      getEmploiDuTemps(id);
    });

    return {
      emploiDuTemps,
      coursGroupes,
      format_date,
    };
  },
});
</script>

<style scoped>

.info-card {
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.cours-card {
  transition: all 0.3s ease;
}

.cours-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.cours-header {
   background-color: #0d1b2a ; 
  color: #ffffff !
}

.cours-header i {
  color: #ffffff ;
}

.cours-header h6 {
  color: #ffffff ; 
}

.cours-header:hover {
  background-color: #112d4e ; 
}

.creneau-card {
  transition: all 0.2s ease;
}

.creneau-card:hover {
  background-color: #e9ecef ;
  border-color: #667eea ;
}

.info-item small {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.875rem;
  padding: 0.35em 0.65em;
}
</style>